import React from 'react';
import { Users } from 'lucide-react';
import SEO from '../../components/SEO';
import FAQRichSnippet from '../../components/FAQRichSnippet';

import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';

const OMHC_FAQS = [
  { q: "What is an OMHC?", a: "An Outpatient Mental Health Clinic (OMHC) is a licensed facility that provides therapy, psychiatric evaluation, and other behavioral health services on an outpatient basis. You visit for scheduled appointments and return home the same day." },
  { q: "What types of therapy do you offer?", a: "We offer individual therapy, family therapy, group therapy, trauma-informed care (including EMDR), cognitive behavioral therapy (CBT), and dialectical behavior therapy (DBT), among other evidence-based approaches." },
  { q: "Do I need a referral to start therapy?", a: "No referral is required. You can contact us directly by phone at (877) 772-5150 or submit an intake form online to get started." },
  { q: "Do you accept Medicaid?", a: "Yes. SDORA Wellbeing accepts Medicaid, Maryland HealthChoice managed care organizations, Medicare, and most major commercial insurance plans." },
  { q: "How long does each therapy session last?", a: "Individual therapy sessions typically last 45 to 60 minutes. Family and group sessions may vary. Your clinician will discuss scheduling during your first visit." },
];

const OutpatientMentalHealthPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO title="OMHC Therapy Services" image="https://static.prod-images.emergentagent.com/jobs/01501ca6-ddde-43b6-bfc5-0444b2f0bae0/images/8f40770aa3732c0ded5f16029f3bd884108c3e635e042fe08e5918ab5337f64e.jpeg" description="Individual therapy, family therapy, trauma-informed care, CBT, DBT, and psychiatric evaluation at SDORA Wellbeing's outpatient mental health clinic in Pikesville, MD. Accepting Medicaid." path="/services/outpatient-mental-health" />
      <Navigation />
      
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <Users className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Outpatient Mental Health Clinic
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl">
            Individual therapy, group therapy, family therapy, couple therapy, and trauma-based/informed therapy for adults and minors.
          </p>
        </div>
      </div>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Services</h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Individual Sessions</h3>
                    <p className="text-gray-600">One-on-one therapy tailored to your unique needs and goals.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Group Therapy</h3>
                    <p className="text-gray-600">Connect with others facing similar challenges in a supportive environment.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Family Counseling</h3>
                    <p className="text-gray-600">Strengthen family bonds and improve communication together.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Trauma-Focused</h3>
                    <p className="text-gray-600">Evidence-based trauma treatment for healing and recovery.</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-blue-50 p-8 rounded-xl">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Who We Help</h2>
              <p className="text-gray-700 mb-4">Our outpatient mental health services are designed for individuals and families seeking support for:</p>
              <ul className="space-y-2 text-gray-700">
                <li>• Depression and anxiety</li>
                <li>• Relationship challenges</li>
                <li>• Trauma and PTSD</li>
                <li>• Life transitions</li>
                <li>• Behavioral concerns</li>
                <li>• Grief and loss</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <FAQRichSnippet faqs={OMHC_FAQS} />
      </div>
      <Footer />
    </div>
  );
};

export default OutpatientMentalHealthPage;